import { useEffect, useRef, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { Grid, IconButton, InputAdornment, Typography, useMediaQuery, useTheme } from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';

import { HeadingTag } from '@/types';
import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';
import TAGButton from '@/components/TAGButton';
import TAGTextFieldInput from '@/components/TAGTextFieldInput';
import { BffBrandType, BffFacility, Header, Maybe } from '@/types/generated';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { getBffFacilitiesByAddress } from '@/services/getBffFacilitiesByAddress';
import { useAppContext, useFacilityContext } from '@/context';
import TAGLink from '@/components/TAGLink';
import { mapSearchClickEvent, searchResultsEvent } from '@/utils';

import AutocompleteAddressTextField from '../AutocompleteAddressTextField/AutocompleteAddressTextField';

import {
	locationSearchCtrStyles,
	locationSearchInputAndBtnCtrStyles,
	locationSearchLocateMeBtnStyles,
	getLocationSearchTextInputStyles,
	getHeadingStyles,
} from './LocationSearch.styles';

export interface ILocationSearch {
	title?: Maybe<string>;
	titleHtag?: Maybe<HeadingTag>;
	updateDataFromLocationSearch: (facilitiesByAddress: BffFacility[]) => void;
	revertToCurrentFacility: () => void;
	onSearchQueryChange?: (searchQuery: string) => void;
	searchQueryValueFromUrl: string;
	isLocationCardsInHeader: boolean;
	locationBarFindOfficeUrl?: Header['locationBarFindOfficeUrl'];
	useGoogleSearch?: boolean;
	googleMapApiKey?: string;
	setForceZoomOut?: (forceZoomOut: boolean) => void;
}

type PermissionStatus = {
	state: 'granted' | 'prompt' | 'denied';
	addEventListener: (type: 'change', listener: () => void) => void;
	removeEventListener: (type: 'change', listener: () => void) => void;
};

export default function LocationSearch(props: Readonly<ILocationSearch>) {
	const {
		title = 'Find a location',
		titleHtag = 'h1',
		updateDataFromLocationSearch,
		revertToCurrentFacility,
		searchQueryValueFromUrl = '',
		isLocationCardsInHeader = false,
		locationBarFindOfficeUrl = '',
		useGoogleSearch = false,
		googleMapApiKey,
		setForceZoomOut,
	} = props;
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { facilityBrand } = useFacilityContext();
	const { environment, config, appName, appVersion } = useAppContext();
	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const fetchAllLocationsOnEmptyResults = config.featureFlags?.fetchAllLocationsOnEmptyResults;
	const placeholder = 'ZIP code, City or State';

	const inputProps = { 'data-testid': 'locations_search_input_box', 'aria-label': placeholder };

	const [searchQuery, setSearchQuery] = useState('');
	const [isGeolocationAvailable, setIsGeolocationAvailable] = useState(true);

	// Bumped on every request that actually goes out, so a response can tell
	// whether a newer search has started since it left.
	const searchIdRef = useRef(0);

	const isNavigatorPermissionsSupported = (): boolean => {
		return typeof navigator !== 'undefined' && typeof navigator.permissions !== 'undefined';
	};

	useEffect(() => {
		if (searchQueryValueFromUrl) {
			setSearchQuery(searchQueryValueFromUrl);
		}
	}, [searchQueryValueFromUrl]);

	useEffect(() => {
		let permissionResult: PermissionStatus | null = null;

		const handlePermissionChange = () => {
			if (permissionResult) {
				setIsGeolocationAvailable(permissionResult.state !== 'denied');
			}
		};

		const checkGeolocationPermission = async () => {
			if (!isNavigatorPermissionsSupported()) {
				// If the Permissions API is not supported, fall back to a default assumption
				setIsGeolocationAvailable(true);
				return;
			}

			try {
				const result = await navigator.permissions.query({ name: 'geolocation' });
				permissionResult = result;

				if (result.state === 'denied') {
					setIsGeolocationAvailable(false);
				}

				result.addEventListener('change', handlePermissionChange);
			} catch (error) {
				// If there's an error (e.g., Permissions API throws), assume geolocation is available
				console.error('Error checking geolocation permissions:', error);
				setIsGeolocationAvailable(true);
			}
		};

		// Invoke the async function, properly ignoring the returned promise with `void`.
		void checkGeolocationPermission();

		return () => {
			if (permissionResult) {
				permissionResult.removeEventListener('change', handlePermissionChange);
			}
		};
	}, []);

	/**
	 * Searches can overlap: the input fires on every Enter press and icon click with no
	 * debounce, disabled state, or spinner, so a slow request can resolve after a newer one.
	 * Only the newest search is allowed to write state — a stale response landing last would
	 * otherwise leave the map and the cards list showing offices for a query the user has
	 * already replaced. Stale searches are still reported to analytics, because the user did
	 * run that search and GA4 should not undercount them.
	 */
	const fetchBffFacilityData = async (queryValue?: string) => {
		const queryString = (queryValue ?? searchQuery).trim();
		if (!queryString || queryString === '') {
			return clearAndRevertToSelectedFacility();
		}

		const searchId = ++searchIdRef.current;
		const isStale = () => searchId !== searchIdRef.current;

		// First try searching by address
		let facilitiesList = await getBffFacilitiesByAddress(
			facilityBrand.toString() as BffBrandType,
			queryString,
			url,
			false,
			appName,
			appVersion
		);

		const shouldZoomOut = facilitiesList?.facilitiesByAddress?.length === 0;

		if (fetchAllLocationsOnEmptyResults && facilitiesList?.facilitiesByAddress?.length === 0) {
			// If no results, fetch all facilities
			facilitiesList = await getBffFacilitiesByAddress(
				facilityBrand.toString() as BffBrandType,
				queryString,
				url,
				true,
				appName,
				appVersion
			);
		}

		const results = (facilitiesList?.facilitiesByAddress as BffFacility[]) || [];

		if (!isStale()) {
			setForceZoomOut?.(shouldZoomOut);
			updateDataFromLocationSearch(results);
		}

		mapSearchClickEvent({ numResults: results.length });
		// Reports what the user sees. With `fetchAllLocationsOnEmptyResults` on, a zero-match
		// search is backfilled with every facility and the map renders them, so the count is
		// that list — brands with the flag off render the no-results view and report 0.
		searchResultsEvent({ searchTerm: queryString, searchResultCount: results.length });
	};

	const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.currentTarget.value);
		if (props.onSearchQueryChange) {
			props.onSearchQueryChange(e.currentTarget.value);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			void fetchBffFacilityData();
		}
	};

	const handleInputIconClick = () => {
		void fetchBffFacilityData();
	};

	const clearAndRevertToSelectedFacility = () => {
		// Invalidate any in-flight search so its response cannot repopulate the map after
		// the user has deliberately cleared it or asked to be relocated.
		searchIdRef.current++;
		mapSearchClickEvent();
		setSearchQuery('');
		revertToCurrentFacility();
	};

	return (
		<Grid
			item
			container
			data-testid="locations_search"
			display="flex"
			flexDirection="column"
			sx={locationSearchCtrStyles(theme, isLocationCardsInHeader)}
		>
			{/* title + locate me */}
			<Grid item width={'100%'}>
				<Grid
					container
					data-testid="locations_search_title_and_button"
					justifyContent={!title ? 'flex-end' : 'space-between'}
					alignItems="center"
					flexWrap={isLocationCardsInHeader ? 'wrap' : 'nowrap'}
				>
					<Grid>
						<Typography
							data-testid="locations_search_title"
							variant={getHeadingVariant(titleHtag as HeadingTag)}
							component={titleHtag === 'normal' ? 'p' : titleHtag === null ? 'h1' : titleHtag}
							sx={getHeadingStyles(theme, isSmallScreen ? 'header3' : 'header2')}
						>
							{title}
						</Typography>
					</Grid>
					{isLocationCardsInHeader ? (
						<Grid item xs={12}>
							<TAGLink
								openInNewTab={false}
								href={locationBarFindOfficeUrl || ''}
								dataTestId="map_view_link"
							>
								<TAGButton
									variant="tertiaryDefault"
									size="S"
									startIcon={<TAGSvgIcon icon="Map" size={20} />}
								>
									Map View
								</TAGButton>
							</TAGLink>
						</Grid>
					) : (
						<Grid>
							{isGeolocationAvailable && (
								<TAGButton
									size="S"
									variant="tertiaryDefault"
									aria-label="Locate me"
									startIcon={<TAGSvgIcon icon={'MapMarker'} size={20} />}
									onClick={() => clearAndRevertToSelectedFacility()}
									sx={locationSearchLocateMeBtnStyles(isLocationCardsInHeader)}
								>
									Locate me
								</TAGButton>
							)}
						</Grid>
					)}
				</Grid>
			</Grid>
			{/* search bar and button*/}
			<Grid item width={'100%'}>
				<Grid
					container
					data-testid="locations_search_input_and_button"
					sx={locationSearchInputAndBtnCtrStyles(isLocationCardsInHeader)}
				>
					<Grid item xs={12}>
						{useGoogleSearch && googleMapApiKey ? (
							<APIProvider apiKey={googleMapApiKey}>
								<AutocompleteAddressTextField
									value={searchQuery}
									onChange={(newValue: string) => {
										setSearchQuery(newValue);
										if (props.onSearchQueryChange) {
											props.onSearchQueryChange(newValue);
										}
									}}
									onSearch={(value?: string) => {
										if (value) {
											setSearchQuery(value);
											if (props.onSearchQueryChange) {
												props.onSearchQueryChange(value);
											}
										}
										void fetchBffFacilityData(value);
									}}
									level="city"
								/>
							</APIProvider>
						) : (
							<TAGTextFieldInput
								fieldPlaceHolder={placeholder}
								onChange={handleSearchInputChange}
								onKeyDown={handleKeyPress}
								value={searchQuery}
								inputSx={getLocationSearchTextInputStyles(theme)}
								inputProps={inputProps}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											onClick={handleInputIconClick}
											data-testid="locations_search_button_input_icon"
											aria-label="Search"
										>
											<SearchIcon data-testid="locations_search_icon_search" />
										</IconButton>
									</InputAdornment>
								}
							/>
						)}
					</Grid>
					{isLocationCardsInHeader && (
						<Grid item xs={12}>
							<TAGButton
								size="S"
								variant="tertiaryDefault"
								aria-label="Locate me"
								startIcon={<TAGSvgIcon icon={'MapMarker'} size={20} />}
								onClick={() => clearAndRevertToSelectedFacility()}
								sx={locationSearchLocateMeBtnStyles(isLocationCardsInHeader)}
							>
								Use My Current Location
							</TAGButton>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}
